"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FleetAuditEventSchema = exports.MAX_ISSUES_PER_HOUR = exports.CODE_TTL_MS = exports.MAX_CODE_ATTEMPTS = exports.CommandResultSchema = exports.CreateCommandRequestSchema = exports.FleetCommandSchema = exports.FleetImpactSchema = exports.FLEET_QUEUE_TTL_MS = exports.FleetActionIdSchema = exports.FLEET_ACTIONS = exports.FleetTierSchema = exports.FleetCommandStateSchema = exports.FLEET_COMMAND_STATES = void 0;
exports.isTerminalState = isTerminalState;
exports.nextCommandState = nextCommandState;
exports.formatBlastRadius = formatBlastRadius;
exports.sha256Hex = sha256Hex;
exports.codeBindingHash = codeBindingHash;
exports.hashFleetCode = hashFleetCode;
exports.validateCode = validateCode;
const zod_1 = require("zod");
exports.FLEET_COMMAND_STATES = [
    'queued', 'sent', 'acked', 'done', 'failed', 'expired', 'cancelled',
];
exports.FleetCommandStateSchema = zod_1.z.enum(exports.FLEET_COMMAND_STATES);
const TERMINAL = new Set(['done', 'failed', 'expired', 'cancelled']);
function isTerminalState(s) {
    return TERMINAL.has(s);
}
/**
 * The only place a command's state may change.
 *
 * Returns the next state, or null when the transition is illegal — callers
 * treat null as "leave the row alone", which is what makes a duplicate WS
 * delivery harmless. `cancelled` is legal only while the command is still
 * undelivered: once it is on the wire we do not know what the device did.
 *
 * `sent -> done` is deliberate, not a gap. The device posts `acked` and then
 * `done` as two separate HTTP calls, and the result poster retries with
 * backoff and swallows a final failure — so the `acked` call can be lost or
 * arrive out of order while the `done` call still lands. When that happens
 * the outcome must win: a command whose work demonstrably completed must not
 * sit in `sent` until the sweeper marks it `expired`. The consequence is that
 * a command can reach `done` with `ackedAt` still null, which is honest — we
 * genuinely never received the ack. The UI renders `state`, not `ackedAt`, so
 * nothing downstream breaks.
 *
 * `queued -> failed` exists because a tier 3 command refused for an offline
 * device fails without ever being delivered.
 *
 * `acked -> expired` exists because a command that was acked but never
 * completed still expires at its TTL.
 */
const LEGAL = {
    queued: new Set(['sent', 'cancelled', 'expired', 'failed']),
    sent: new Set(['acked', 'done', 'failed', 'expired']),
    acked: new Set(['done', 'failed', 'expired']),
    done: new Set(),
    failed: new Set(),
    expired: new Set(),
    cancelled: new Set(),
};
function nextCommandState(current, event) {
    return LEGAL[current].has(event) ? event : null;
}
exports.FleetTierSchema = zod_1.z.union([zod_1.z.literal(1), zod_1.z.literal(2), zod_1.z.literal(3)]);
const def = (id, tier, label, capability, ttlSeconds) => ({ id, tier, label, capability, needsCode: tier === 3, ttlSeconds });
/** The single place an action's tier is decided. Moving one is one line. */
exports.FLEET_ACTIONS = {
    collect_logs: def('collect_logs', 1, 'Collect logs', 'logs', 300),
    capture_screen: def('capture_screen', 1, 'Capture screen', 'screen', 120),
    device_report: def('device_report', 1, 'Device report', null, 90),
    force_resync: def('force_resync', 1, 'Force re-sync', null, 300),
    impact: def('impact', 1, 'Blast radius probe', null, 45),
    // RubyTill Pay (dejavoo_rubytill_pay) devices — Task 15.
    reload_page: def('reload_page', 1, 'Reload page', null, 60),
    refresh_config: def('refresh_config', 1, 'Refresh settings', null, 60),
    restart: def('restart', 2, 'Restart till', 'reboot', 300),
    lock: def('lock', 2, 'Lock till', null, 300),
    unlock: def('unlock', 2, 'Unlock till', null, 300),
    apply_policy: def('apply_policy', 2, 'Apply lockdown policy', 'policy', 300),
    push_version: def('push_version', 2, 'Push app version', 'ota', 3600),
    update_now: def('update_now', 2, 'Update now', 'ota', 3600),
    clear_pairing: def('clear_pairing', 3, 'Clear pairing', 'clearData', 600),
    factory_wipe: def('factory_wipe', 3, 'Factory wipe', 'wipe', 600),
};
exports.FleetActionIdSchema = zod_1.z.enum(Object.keys(exports.FLEET_ACTIONS));
/**
 * D3 fix (2026-09-20): "Tier 1/2: queue, 24h TTL, fire on reconnect" (spec
 * §5). A tier 1/2 command that cannot be delivered at create time gets this
 * as its delivery deadline. It is NOT the action's own `ttlSeconds` — that
 * would expire most diagnostics in minutes, long before a till plausibly
 * comes back online.
 */
exports.FLEET_QUEUE_TTL_MS = 24 * 60 * 60 * 1000;
exports.FleetImpactSchema = zod_1.z.object({
    unsyncedSaleCount: zod_1.z.number().int().min(0),
    /** Minor units (cents). Never a float — this is money. */
    unsyncedSaleValue: zod_1.z.number().int().min(0),
    openShift: zod_1.z.object({
        employeeName: zod_1.z.string().max(200),
        startedAt: zod_1.z.number().int(),
    }).nullable().default(null),
    capturedAt: zod_1.z.number().int(),
    /** True when this is a remembered figure, not one the device just gave us. */
    stale: zod_1.z.boolean().default(false),
});
const money = (cents) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
/**
 * The lines the destructive dialog shows. Pure and string-only so the wording
 * is testable without a browser — this text is the last thing between an
 * operator and an erased till.
 */
function formatBlastRadius(impact) {
    const lines = [];
    if (impact.unsyncedSaleCount > 0) {
        const s = impact.unsyncedSaleCount === 1 ? 'sale' : 'sales';
        lines.push(`${impact.unsyncedSaleCount} ${s} recorded on the till and not yet synced — ${money(impact.unsyncedSaleValue)}`);
    }
    else {
        lines.push('No unsynced sales on the till');
    }
    if (impact.openShift) {
        lines.push(`An open shift for ${impact.openShift.employeeName}`);
    }
    lines.push('The register pairing, PIN roster and layout');
    return lines;
}
exports.FleetCommandSchema = zod_1.z.object({
    commandId: zod_1.z.string().min(1).max(64),
    deviceId: zod_1.z.string().min(1).max(256),
    deviceType: zod_1.z.string().min(1).max(256),
    tier: exports.FleetTierSchema,
    action: exports.FleetActionIdSchema,
    params: zod_1.z.record(zod_1.z.unknown()).default({}),
    state: exports.FleetCommandStateSchema,
    requestedBy: zod_1.z.string().min(1).max(256),
    reason: zod_1.z.string().max(500).nullable().default(null),
    authCodeId: zod_1.z.string().max(64).nullable().default(null),
    createdAt: zod_1.z.number().int(),
    sentAt: zod_1.z.number().int().nullable().default(null),
    ackedAt: zod_1.z.number().int().nullable().default(null),
    completedAt: zod_1.z.number().int().nullable().default(null),
    result: zod_1.z.record(zod_1.z.unknown()).nullable().default(null),
    failureReason: zod_1.z.string().max(1000).nullable().default(null),
    /**
     * D3 fix (2026-09-20): `expiresAt` means "the deadline for the CURRENT
     * state" — it is deliberately reused rather than given a second field,
     * because the `byState` GSI sorts on `expiresAt` and a second TTL field
     * would not be sweepable by that index. Its meaning changes with state:
     *
     *   - `queued` (never delivered): the DELIVERY deadline —
     *     `createdAt + FLEET_QUEUE_TTL_MS` (24h). "Fire on reconnect" within
     *     this window is what pos-fleet-support-service's
     *     `/internal/devices/{deviceId}/redeliver` implements.
     *   - `sent` (delivered, awaiting ack/done): the EXECUTION deadline —
     *     rewritten to `now + FLEET_ACTIONS[action].ttlSeconds * 1000` the
     *     instant delivery succeeds, whether that happens at create time or
     *     later via redeliver-on-reconnect.
     *
     * A future reader adding a new transition must rewrite `expiresAt` to
     * mean the deadline for THAT new state, not assume one TTL for the whole
     * command lifetime.
     */
    expiresAt: zod_1.z.number().int(),
    ttl: zod_1.z.number().int(),
});
exports.CreateCommandRequestSchema = zod_1.z.object({
    deviceId: zod_1.z.string().min(1).max(256),
    deviceType: zod_1.z.string().min(1).max(256),
    action: exports.FleetActionIdSchema,
    params: zod_1.z.record(zod_1.z.unknown()).optional(),
    reason: zod_1.z.string().min(1).max(500).optional(),
    codeId: zod_1.z.string().max(64).optional(),
    code: zod_1.z.string().length(6).optional(),
}).superRefine((v, ctx) => {
    if (exports.FLEET_ACTIONS[v.action].tier === 3) {
        for (const k of ['reason', 'codeId', 'code']) {
            if (!v[k]) {
                ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: [k],
                    message: `${k} is required for a tier 3 action` });
            }
        }
    }
});
exports.CommandResultSchema = zod_1.z.object({
    state: zod_1.z.enum(['acked', 'done', 'failed']),
    result: zod_1.z.record(zod_1.z.unknown()).optional(),
    failureReason: zod_1.z.string().max(1000).optional(),
});
// ============================================================
// Task 15 — Tier-3 authorisation codes: binding + validation.
//
// Pure and TDD'd. No network, no AWS, no React — but `fleetSupport.ts` is
// imported into browser bundles (super-admin-ui, AGENTIC_POS) as well as
// this service's own Node runtime, so it may NOT import `node:crypto` at
// module scope: that import is unresolvable in a browser bundle and would
// break every consumer of this file, not just the ones that call
// `codeBindingHash`/`validateCode`. `sha256Hex` below is a small
// dependency-free SHA-256 (bytes in via the global `TextEncoder`, hex out)
// so this stays portable everywhere `@platform/shared-types` ships.
// ============================================================
exports.MAX_CODE_ATTEMPTS = 5;
exports.CODE_TTL_MS = 10 * 60 * 1000;
exports.MAX_ISSUES_PER_HOUR = 5;
const SHA256_K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];
function rotr(x, n) {
    return (x >>> n) | (x << (32 - n));
}
/** Pure, dependency-free SHA-256. String in, lowercase hex out. */
function sha256Hex(message) {
    const bytes = new TextEncoder().encode(message);
    const h = new Uint32Array([
        0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
        0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
    ]);
    const bitLen = bytes.length * 8;
    const paddedLen = Math.ceil((bytes.length + 1 + 8) / 64) * 64;
    const padded = new Uint8Array(paddedLen);
    padded.set(bytes);
    padded[bytes.length] = 0x80;
    const dv = new DataView(padded.buffer);
    dv.setUint32(paddedLen - 4, bitLen >>> 0, false);
    dv.setUint32(paddedLen - 8, Math.floor(bitLen / 0x100000000), false);
    const w = new Uint32Array(64);
    for (let offset = 0; offset < paddedLen; offset += 64) {
        for (let i = 0; i < 16; i++)
            w[i] = dv.getUint32(offset + i * 4, false);
        for (let i = 16; i < 64; i++) {
            const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
            const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
            w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
        }
        let [a, b, c, d, e, f, g, hh] = h;
        for (let i = 0; i < 64; i++) {
            const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
            const ch = (e & f) ^ (~e & g);
            const temp1 = (hh + S1 + ch + SHA256_K[i] + w[i]) | 0;
            const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
            const maj = (a & b) ^ (a & c) ^ (b & c);
            const temp2 = (S0 + maj) | 0;
            hh = g;
            g = f;
            f = e;
            e = (d + temp1) | 0;
            d = c;
            c = b;
            b = a;
            a = (temp1 + temp2) | 0;
        }
        h[0] = (h[0] + a) | 0;
        h[1] = (h[1] + b) | 0;
        h[2] = (h[2] + c) | 0;
        h[3] = (h[3] + d) | 0;
        h[4] = (h[4] + e) | 0;
        h[5] = (h[5] + f) | 0;
        h[6] = (h[6] + g) | 0;
        h[7] = (h[7] + hh) | 0;
    }
    return Array.from(h).map((x) => (x >>> 0).toString(16).padStart(8, '0')).join('');
}
/** Constant-time comparison of two equal-shape hex strings. Never short-circuits on the first mismatch. */
function constantTimeHexEqual(a, b) {
    if (a.length !== b.length)
        return false;
    let diff = 0;
    for (let i = 0; i < a.length; i++)
        diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
    return diff === 0;
}
/**
 * Binds an auth code to exactly the device/action/reason it was issued for.
 * A code for "wipe Register 2, returned to stock" must not validate on
 * Register 5, on a different action, or after the reason text changes.
 */
function codeBindingHash(input) {
    return sha256Hex([input.deviceId, input.action, input.reason].join(' '));
}
/** `codeHash = sha256(code + codeId)` — the codeId salts identical 6-digit codes issued for different devices apart. */
function hashFleetCode(code, codeId) {
    return sha256Hex(`${code}${codeId}`);
}
/**
 * Pure decision on whether a submitted code is good. Never returns the code
 * or its hash — only a `why` enum — so a failure reason is safe to log or
 * return over HTTP without leaking the secret it is refusing.
 *
 * Callers (`codeStore.verifyCode`) are responsible for atomically
 * incrementing `attempts` BEFORE calling this — `stored.attempts` here must
 * already include the attempt being judged, which is what makes "burns on
 * the 5th wrong entry" exact: a 5th attempt that happens to be CORRECT still
 * succeeds; a 5th attempt that is wrong burns the code, and every attempt
 * after that (attempts > MAX_CODE_ATTEMPTS) is 'burned' before anything else
 * is even compared.
 */
function validateCode(stored, given, now) {
    if (stored.consumedAt !== null)
        return { ok: false, why: 'consumed' };
    if (stored.attempts > exports.MAX_CODE_ATTEMPTS)
        return { ok: false, why: 'burned' };
    if (now > stored.expiresAt)
        return { ok: false, why: 'expired' };
    const bindingOk = constantTimeHexEqual(codeBindingHash({ deviceId: given.deviceId, action: given.action, reason: given.reason }), stored.bindingHash);
    if (!bindingOk)
        return { ok: false, why: 'wrong_binding' };
    const codeOk = constantTimeHexEqual(hashFleetCode(given.code, given.codeId), stored.codeHash);
    if (!codeOk) {
        if (stored.attempts >= exports.MAX_CODE_ATTEMPTS)
            return { ok: false, why: 'burned' };
        return { ok: false, why: 'wrong_code' };
    }
    return { ok: true };
}
exports.FleetAuditEventSchema = zod_1.z.object({
    eventId: zod_1.z.string().min(1).max(64),
    deviceId: zod_1.z.string().min(1).max(256),
    at: zod_1.z.number().int(),
    kind: zod_1.z.enum([
        'command_created', 'command_state', 'command_refused',
        'code_issued', 'code_redeemed', 'code_failed', 'code_burned',
    ]),
    actor: zod_1.z.string().min(1).max(256),
    action: zod_1.z.string().max(64).nullable().default(null),
    reason: zod_1.z.string().max(500).nullable().default(null),
    detail: zod_1.z.record(zod_1.z.unknown()).default({}),
});
//# sourceMappingURL=fleetSupport.js.map